/* eslint-disable jsx-a11y/alt-text */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import '../index.css';

const LandingPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogin = () => {
    navigate('/login');
  };

  const handleSignup = () => {
    navigate('/signup');
  };

  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* Header */}
      <header className="bg-white shadow-sm fixed top-0 w-full z-50">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <div className="flex items-center">
              <img src="/main_logo.png" className="h-10 md:h-12" alt="AdBooker Logo" />
            </div>

            {/* Desktop CTA Buttons */}
            <div className="hidden md:flex items-center space-x-3">
              <button
                onClick={handleLogin}
                className="px-4 py-2 text-white  hover:opacity-90 transition-colors"
                style={{ backgroundColor: '#584968' }}
              >
                Log In
              </button>
              <button
                onClick={handleSignup}
                className="px-4 py-2 text-white  hover:opacity-90 transition-colors"
                style={{ backgroundColor: '#7D99FA' }}
              >
                Start your FREE trial now
              </button>
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2  hover:bg-gray-100"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <div className="flex flex-col space-y-3">
                <button
                  onClick={handleLogin}
                  className="px-4 py-2 text-white  text-center"
                  style={{ backgroundColor: '#584968' }}
                >
                  Log In
                </button>
                <button
                  onClick={handleSignup}
                  className="px-4 py-2 text-white  text-center"
                  style={{ backgroundColor: '#7D99FA' }}
                >
                  Start your FREE trial now
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section - White Background */}
      <section className="bg-white pt-20 md:pt-24 lg:pt-32 pb-0">
        <div className="sm:px-6 lg:px-20">
          <div className="flex flex-col-reverse lg:grid lg:grid-cols-2 gap-12 items-start w-full">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight lg:mt-[100px] mt-50px">
                <span style={{ color: '#7D99FA' }}>Smarter</span>{' '}
                <span style={{ color: '#584968' }}> Ad Booking </span>
              </h2>
              <p className="text-2xl lg:text-4xl text-gray-700 mb-8 leading-relaxed">
                <span style={{ color: '#584968' }} className="font-bold">
                  {' '}
                  Clearer
                </span>{' '}
                <span className="font-bold" style={{ color: '#7D99FA' }}>
                  Revenue.
                </span>{' '}
                <span className="font-bold" style={{ color: '#584968' }}>
                  Less
                </span>{' '}
                <span style={{ color: '#7D99FA' }} className="text-purple-500 font-bold">
                  Hassle.
                </span>
              </p>
              <div className="flex justify-center items-center block  lg:hidden">
                <img src="/hero.png" className="max-h-[400px] ml-40 lg:hidden" />
              </div>

              <div className=" bg-[#7D99FA] w-screen lg:mt-[130px] lg:mt-32 -ml-[25px] lg:-ml-[80px] px-[80px] pb-[80px]">
                <p className="text-xl text-white mb-10 leading-relaxed pt-[70px] w-full xl:w-[45%]">
                  AdBooker is the all-in-one system for recording and managing business customer
                  advertisement orders. Whether you run a magazine, newsletter, or newspaper,
                  AdBooker keeps every booking organised and your revenue in clear view.
                </p>
                {/* Features Section - Moved Here */}
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 ">
                  {[
                    {
                      iconSrc: '/Track_Every_Ad.svg',
                      title: 'Track Every Ad',
                      description:
                        "Record exactly what advertisements each customer wants, which magazines issue they'll appear in, and how much they'll pay.",
                    },
                    {
                      iconSrc: '/Instant_Revenue_Insights.svg',
                      title: 'Instant Revenue Insights',
                      description:
                        "See at a glance what's booked and the total revenue for each current issue.",
                    },
                    {
                      iconSrc: '/RenewalsMade_Easy.svg',
                      title: 'Renewals Made Easy',
                      description:
                        'Quickly spot customers who need to renew their bookings before deadlines.',
                    },
                    {
                      iconSrc: '/Leaflet_Insert_Support.svg',
                      title: 'Leaflet Insert Support',
                      description:
                        'Manage special leaflet insert bookings with a dedicated section.',
                    },
                  ].map((feature, index) => (
                    <div
                      key={index}
                      className="text-center group hover:transform hover:scale-105 transition-all duration-300"
                    >
                      <div className="w-32 h-32  flex items-center justify-center mx-auto mb-4  transition-colors">
                        <img src={feature.iconSrc} alt={feature.title} className="w-28 h-28" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                      <p className="text-gray-600 leading-relaxed text-sm">{feature.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="hidden lg:block">
              <img src="/hero.png" style={{ marginLeft: '130px', maxHeight: '600px' }} />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing">
        <div className="w-full">
          <div className="grid lg:grid-cols-2 gap-0 items-stretch">
            {/* Left - Magazine Stack Visual */}
            <div className="relative">
              <img src="/hero_2.jpg" className="w-full h-full object-cover" alt="Magazine stack" />
            </div>

            {/* Right - Pricing Content with Red Background */}
            <div className="p-12 flex flex-col justify-center">
              <div className="bg-white  p-8 shadow-xl">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                  Rates show the monthly subscription price per magazine.
                </h2>

                {/* Pricing Table */}
                <div className="bg-white  shadow-lg overflow-hidden mb-8">
                  <div
                    className="text-white p-4 text-center"
                    style={{ backgroundColor: '#7D99FA' }}
                  >
                    <h3 className="text-xl font-bold">Magazines</h3>
                  </div>
                  <div className="divide-y divide-gray-200">
                    {[
                      { range: '1, 2 or 3', price: '£15.00' },
                      { range: '4, 5 or 6', price: '£13.00' },
                      { range: '7, 8 or 9', price: '£11.00' },
                      {
                        range: '10 or more',
                        price: 'Contact Us',
                        highlight: true,
                      },
                    ].map((tier, index) => (
                      <div
                        key={index}
                        className={`flex justify-between items-center p-4 ${tier.highlight ? 'bg-green-50' : 'hover:bg-gray-50'} transition-colors`}
                      >
                        <span className="text-gray-700 font-medium">{tier.range}</span>
                        <span
                          className={`font-bold text-lg ${tier.highlight ? 'text-green-600' : 'text-blue-600'}`}
                        >
                          {tier.price}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Special Offers */}
                <div className="bg-blue-50  p-6 mb-8 border border-blue-200">
                  <h4 className="font-bold text-gray-900 mb-3">SPECIAL OFFER FOR BI-MONTHLY :</h4>
                  <p className="text-gray-600 mb-3 text-sm">
                    If you only print every other month, we’ll give you 30% off the monthly price.
                  </p>
                  <p className="text-gray-600 text-sm">
                    <strong>EXAMPLE:</strong> 3 bi-monthly magazines would be £31.50 a month (plus
                    VAT).
                  </p>
                </div>

                <button
                  onClick={handleSignup}
                  className="w-full py-4 text-white hover:opacity-90 transition-all text-lg font-bold shadow-lg"
                  style={{ backgroundColor: '#7D99FA' }}
                >
                  Start your FREE trial now
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="text-white" style={{ backgroundColor: '#584968 ' }}>
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12 pt-6 px-">
            {/* Logo and Description */}
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-6">
                <img src="/main_logo_white.png" style={{ height: '50px' }} />
              </div>
              <p className="text-gray-300 mb-8 max-w-md text-lg leading-relaxed">
                The all-in-one system for recording and managing business customer advertisement
                orders. Keep your bookings organised and revenue clear.
              </p>
            </div>

            {/* Contact */}
            <div>
              <h3 className="text-xl font-bold mb-6">Contact</h3>
              <ul className="space-y-4 text-gray-300 text-lg">
                <li>adbooker@justbright.co.uk</li>
                <li>02382 120033</li>
                <li>Mon-Fri 9AM-5PM GMT</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-400 mt-12 pt-8 text-center text-gray-200 text-lg py-8">
            <p>&copy; {new Date().getFullYear()} Justbright Ltd. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
